<app-loader [isLoading]="isLoading"></app-loader>

<div class="signups">
  <div class="signups__header">
    <div>
      <h4 class="signups__title">{{ "tenantSignups.Title" | translate }}</h4>
      <p class="signups__subtitle">{{ "tenantSignups.Subtitle" | translate }}</p>
    </div>
    <div class="signups__filter">
      <label class="form-label mb-0 me-2">{{ "tenantSignups.Status" | translate }}</label>
      <select class="form-select" [(ngModel)]="statusFilter" (change)="onFilterChange()">
        <option value="">{{ "tenantSignups.All" | translate }}</option>
        <option value="Pending">{{ "tenantSignups.Pending" | translate }}</option>
        <option value="Approved">{{ "tenantSignups.Approved" | translate }}</option>
        <option value="Rejected">{{ "tenantSignups.Rejected" | translate }}</option>
      </select>
    </div>
  </div>

  <div class="table-responsive signups__table">
    <table class="table align-middle">
      <thead>
        <tr>
          <th>{{ "tenantSignups.Organization" | translate }}</th>
          <th>{{ "tenantSignups.Contact" | translate }}</th>
          <th>{{ "tenantSignups.Subdomain" | translate }}</th>
          <th>{{ "tenantSignups.Date" | translate }}</th>
          <th>{{ "tenantSignups.Status" | translate }}</th>
          <th class="text-end">{{ "tenantSignups.Actions" | translate }}</th>
        </tr>
      </thead>
      <tbody>
        <ng-container *ngFor="let req of requests">
          <tr>
            <td>
              <button type="button" class="btn btn-link p-0 fw-semibold" (click)="toggleDetails(req.id)">
                {{ req.organizationName }}
              </button>
            </td>
            <td>
              <div>{{ req.contactName }}</div>
              <div class="text-muted small">{{ req.email }}</div>
            </td>
            <td>{{ req.preferredSubdomain || '—' }}</td>
            <td>{{ req.createdAt | localizedDate: 'datetime' }}</td>
            <td><span [class]="badgeClass(req.status)">{{ req.status }}</span></td>
            <td class="text-end">
              <button type="button" class="btn btn-sm btn-outline-success me-1"
                [disabled]="req.status === 'Approved'"
                (click)="openApproveDialog(req)">
                {{ "tenantSignups.Approve" | translate }}
              </button>
              <button type="button" class="btn btn-sm btn-outline-secondary me-1"
                *ngIf="req.isProvisioned"
                (click)="resend(req)">
                {{ "tenantSignups.ResendLink" | translate }}
              </button>
              <button type="button" class="btn btn-sm btn-outline-danger me-1"
                [disabled]="req.status === 'Rejected'"
                (click)="reject(req)">
                {{ "tenantSignups.Reject" | translate }}
              </button>
              <button type="button" class="btn btn-sm btn-outline-danger"
                (click)="deleteRequest(req)" [title]="'tenantSignups.Delete' | translate">
                {{ "tenantSignups.Delete" | translate }}
              </button>
            </td>
          </tr>
          <tr *ngIf="expandedId === req.id" class="signups__details-row">
            <td colspan="6">
              <div class="signups__details">
                <div><strong>{{ "tenantSignups.Phone" | translate }}:</strong> {{ req.phone || '—' }}</div>
                <div><strong>{{ "tenantSignups.OrganizationType" | translate }}:</strong> {{ req.organizationType || '—' }}</div>
                <div><strong>{{ "tenantSignups.Message" | translate }}:</strong> {{ req.message || '—' }}</div>
              </div>
            </td>
          </tr>
        </ng-container>

        <tr *ngIf="!isLoading && requests.length === 0">
          <td colspan="6" class="text-center text-muted py-4">
            {{ "tenantSignups.Empty" | translate }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- Approve / provision dialog -->
  <div class="signups__backdrop" *ngIf="approveTarget" (click)="closeApproveDialog()"></div>
  <div class="signups__modal" *ngIf="approveTarget" role="dialog" aria-modal="true">
    <div class="signups__modal-head">
      <h5 class="mb-0">{{ "tenantSignups.ApproveTitle" | translate }}</h5>
      <button type="button" class="btn-close" (click)="closeApproveDialog()"></button>
    </div>
    <div class="signups__modal-body">
      <p class="text-muted small mb-3">{{ "tenantSignups.ApproveHint" | translate }}</p>

      <div class="mb-2">
        <strong>{{ approveTarget.organizationName }}</strong>
        <div class="text-muted small">{{ approveTarget.contactName }} · <span dir="ltr">{{ approveTarget.email }}</span></div>
      </div>

      <label class="form-label mb-1">{{ "tenantSignups.Subdomain" | translate }}</label>
      <div class="input-group">
        <input type="text" class="form-control" [(ngModel)]="subdomainInput"
          (ngModelChange)="onSubdomainInput()" autocomplete="off" spellcheck="false" />
      </div>

      <div class="signups__check small mt-2">
        <span *ngIf="checking" class="text-muted">
          <span class="spinner-border spinner-border-sm me-1"></span>{{ "tenantSignups.Checking" | translate }}
        </span>
        <span *ngIf="!checking && checkResult?.available" class="text-success">
          ✓ {{ "tenantSignups.Available" | translate }}
        </span>
        <span *ngIf="!checking && checkResult && !checkResult.available" class="text-danger">
          ✕ {{ checkResult.reasonEn }}
        </span>
      </div>

      <!-- What the new tenant uses: pre-filled from the organisation type they gave -->
      <div class="signups__pillars">
        <p class="text-muted small mb-2">
          {{ "tenantModules.ApproveHint" | translate }}
          <strong *ngIf="approveTarget.organizationType">“{{ approveTarget.organizationType }}”</strong>
        </p>
        <app-tenant-modules-picker
          [compact]="true"
          [type]="approveType"
          [modules]="approveModules"
          [disabled]="submitting"
          (typeChange)="approveType = $event"
          (modulesChange)="approveModules = $event">
        </app-tenant-modules-picker>
      </div>
    </div>
    <div class="signups__modal-foot">
      <button type="button" class="btn btn-outline-secondary" (click)="closeApproveDialog()">
        {{ "tenantSignups.Cancel" | translate }}
      </button>
      <button type="button" class="btn btn-success" [disabled]="!canConfirmApprove" (click)="confirmApprove()">
        <span *ngIf="submitting" class="spinner-border spinner-border-sm me-1"></span>
        {{ "tenantSignups.ApproveConfirm" | translate }}
      </button>
    </div>
  </div>

  <div class="signups__pagination" *ngIf="totalPages > 1">
    <button class="btn btn-sm btn-outline-secondary"
      [disabled]="pageNumber <= 1" (click)="goToPage(pageNumber - 1)">
      {{ "tenantSignups.Prev" | translate }}
    </button>
    <span class="signups__page-info" dir="ltr">{{ pageNumber }} / {{ totalPages }}</span>
    <button class="btn btn-sm btn-outline-secondary"
      [disabled]="pageNumber >= totalPages" (click)="goToPage(pageNumber + 1)">
      {{ "tenantSignups.Next" | translate }}
    </button>
  </div>
</div>
